import { useEffect, useMemo, useState } from 'react';
import { useChatDispatch, useChatSelector } from '../store/hooks';
import {
    contextAdded,
    contextRemoved,
    contextRemovedByTypeUri,
    contextsCleared,
    contextUpdated,
    pageMetaSet,
    selectContextCount,
    selectContexts,
    selectCurrentPageMeta,
    selectSendPayloadContexts,
} from './chatContextSlice';
import { detectContext } from './detectors';

export function useChatContexts() {
    return useChatSelector(selectContexts);
}

export function useChatContextCount() {
    return useChatSelector(selectContextCount);
}

export function useChatContextSendPayload() {
    return useChatSelector(selectSendPayloadContexts);
}

export function useChatContextActions() {
    const dispatch = useChatDispatch();
    return useMemo(
        () => ({
            add: (ctx) => dispatch(contextAdded(ctx)),
            remove: (key) => dispatch(contextRemoved(key)),
            removeByTypeUri: (type, uri) => dispatch(contextRemovedByTypeUri({ type, uri })),
            clear: () => dispatch(contextsCleared()),
            update: (key, patch) => dispatch(contextUpdated({ key, patch })),
        }),
        [dispatch],
    );
}

export function useChatContext({ type, uri, label, link, source = 'auto', enabled = true }) {
    const dispatch = useChatDispatch();

    useEffect(() => {
        if (!enabled || !type || !uri) return undefined;
        dispatch(contextAdded({ type, uri, label, link, source }));
        return () => dispatch(contextRemovedByTypeUri({ type, uri }));
    }, [dispatch, enabled, type, uri, source]);

    useEffect(() => {
        if (!enabled || !type || !uri) return;
        dispatch(contextUpdated({ key: `${type}:${uri}`, patch: { label, link } }));
    }, [dispatch, enabled, type, uri, label, link]);
}

function readBrowserLocation() {
    if (typeof window === 'undefined') return { pathname: '', search: '' };
    return {
        pathname: window.location.pathname || '',
        search: window.location.search || '',
    };
}

export function useCurrentPageContext() {
    const [loc, setLoc] = useState(readBrowserLocation);
    const items = useChatSelector(selectContexts);
    const pageMeta = useChatSelector(selectCurrentPageMeta);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const sync = () => setLoc(readBrowserLocation());
        window.addEventListener('popstate', sync);
        window.addEventListener('pushstate', sync);
        window.addEventListener('replacestate', sync);
        const originalPush = window.history.pushState;
        const originalReplace = window.history.replaceState;
        window.history.pushState = function patchedPush(...args) {
            const ret = originalPush.apply(this, args);
            window.dispatchEvent(new Event('pushstate'));
            return ret;
        };
        window.history.replaceState = function patchedReplace(...args) {
            const ret = originalReplace.apply(this, args);
            window.dispatchEvent(new Event('replacestate'));
            return ret;
        };
        return () => {
            window.removeEventListener('popstate', sync);
            window.removeEventListener('pushstate', sync);
            window.removeEventListener('replacestate', sync);
            window.history.pushState = originalPush;
            window.history.replaceState = originalReplace;
        };
    }, []);

    return useMemo(() => {
        const detected = detectContext(loc);
        if (!detected) return null;
        const pathMatches = pageMeta?.pathname && pageMeta.pathname === loc.pathname;
        if (pathMatches && pageMeta.title) {
            return { ...detected, label: pageMeta.title, isLoading: false };
        }
        const stored = items.find((c) => c.type === detected.type && c.uri === detected.uri);
        if (stored?.label && stored.label !== stored.uri) {
            return { ...detected, label: stored.label, isLoading: false };
        }
        return { ...detected, label: null, isLoading: true };
    }, [loc.pathname, loc.search, items, pageMeta?.title, pageMeta?.pathname]);
}

export function usePageTitle(title) {
    const dispatch = useChatDispatch();

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const pathname = window.location.pathname || '';
        if (!title) {
            dispatch(pageMetaSet({ title: null }));
            return undefined;
        }
        dispatch(pageMetaSet({ title, pathname }));
        return () => {
            dispatch(pageMetaSet({ title: null }));
        };
    }, [dispatch, title]);
}

export function usePageSuggestions(suggestions) {
    const dispatch = useChatDispatch();
    const serialized = useMemo(
        () => (Array.isArray(suggestions) ? suggestions : null),
        [suggestions],
    );

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const pathname = window.location.pathname || '';
        if (serialized === null) {
            dispatch(pageMetaSet({ suggestions: null, suggestionsStatus: 'loading', pathname }));
        } else {
            dispatch(pageMetaSet({ suggestions: serialized, suggestionsStatus: 'ready', pathname }));
        }
    }, [dispatch, serialized]);

    useEffect(() => {
        return () => {
            dispatch(pageMetaSet({ suggestions: null, suggestionsStatus: 'idle' }));
        };
    }, [dispatch]);
}
